/**
 * Utilities for diffing id-keyed arrays against a Y.Map and reading
 * the map back into an array.
 *
 * DESIGN: we store each node/edge as a plain JSON value (`unknown`)
 * in the Y.Map rather than a nested Y.Map<field→value>. This gives
 * coarser granularity (any change to a node replaces the whole
 * object) but is dramatically simpler to reason about and avoids the
 * nested-observe complexity. Granular per-field CRDT is a v2
 * optimisation (see PLAN.md item #3a).
 */
import * as Y from "yjs";

/** Any object with a stable string `id` field. */
export interface WithId {
  readonly id: string;
}

/**
 * xyflow-computed / interaction fields that must NOT be persisted to Yjs or
 * tracked by the UndoManager. They are DERIVED, per-client, and change on every
 * render — `measured` (the node's rendered size) alone re-fires on every resize,
 * zoom, or content reflow, so syncing it floods the doc and litters undo history
 * with net-zero layout noise (a resize that changes nothing still bumps
 * `measured.height`, minting an undo step). Only the authored fields (id, type,
 * position, data, …) should round-trip; xyflow recomputes the rest on load.
 */
const VOLATILE_FIELDS: ReadonlySet<string> = new Set([
  "measured", // rendered {width,height} — the resize/zoom/reflow culprit
  "width", // v12 rendered width (measured is canonical; not authored here)
  "height", // v12 rendered height
  "internals", // xyflow internal bookkeeping
  "selected", // local selection state
  "dragging", // live drag flag
  "resizing", // live resize flag
  "positionAbsolute", // computed from position + parent
  "handleBounds", // computed handle geometry
]);

/** Shallow copy without the volatile/computed xyflow fields. */
export function stripVolatile<T extends WithId>(item: T): T {
  let copy: Record<string, unknown> | null = null;
  for (const k of Object.keys(item)) {
    if (VOLATILE_FIELDS.has(k)) {
      if (copy === null) copy = { ...(item as Record<string, unknown>) };
      delete copy[k];
    }
  }
  return (copy ?? item) as T;
}

/**
 * Carry each item's volatile/per-client fields (selection, live drag/resize
 * flags, measured size, …) from a PREVIOUS array onto a freshly-read `next`
 * array, matched by id.
 *
 * A remote peer authors only the persisted fields, so {@link readMap} rebuilds
 * items WITHOUT the volatile ones. Applying that array verbatim would clobber
 * THIS client's transient UI state — most visibly, dragging a node round-trips
 * its position through the doc and the echo would wipe the node's `selected`
 * flag (and force a re-measure). Re-attaching the local volatile values keeps
 * selection, an in-flight drag, and rendered size stable across remote updates.
 */
export function preserveVolatile<T extends WithId>(
  next: ReadonlyArray<T>,
  prev: ReadonlyArray<T>,
): T[] {
  const prevById = new Map<string, Record<string, unknown>>(
    prev.map((x) => [x.id, x as Record<string, unknown>]),
  );
  return next.map((item) => {
    const before = prevById.get(item.id);
    if (!before) return item;
    let merged: Record<string, unknown> | null = null;
    for (const k of VOLATILE_FIELDS) {
      if (before[k] !== undefined) {
        merged ??= { ...(item as Record<string, unknown>) };
        merged[k] = before[k];
      }
    }
    return (merged ?? item) as T;
  });
}

/**
 * Diff two id-keyed arrays and apply the delta to `ymap`.
 *
 * Operations (in order):
 *   1. Delete keys present in `prev` but absent in `next`.
 *   2. Set keys that are new or whose (volatile-stripped) value changed.
 *
 * Both sides are run through {@link stripVolatile} and compared by VALUE
 * (deep), so re-renders that only touch xyflow's computed fields — or that
 * rebuild an object with identical values — never write. This is what keeps a
 * node resize/zoom (which bumps `measured`) out of the undo history.
 *
 * MUST be called inside a `ydoc.transact` block tagged with
 * LOCAL_ORIGIN — the caller's responsibility.
 */
export function diffIntoMap<T extends WithId>(
  prev: ReadonlyArray<T>,
  next: ReadonlyArray<T>,
  ymap: Y.Map<unknown>,
): void {
  const prevById = new Map<string, T>(
    prev.map((x) => [x.id, stripVolatile(x)]),
  );
  const nextById = new Map<string, T>(
    next.map((x) => [x.id, stripVolatile(x)]),
  );

  // Deleted items
  for (const id of prevById.keys()) {
    if (!nextById.has(id)) ymap.delete(id);
  }

  // Added or changed items — deep compare skips no-ops (incl. new-ref/same-value
  // rebuilds and pure `measured` churn).
  for (const [id, item] of nextById) {
    const before = prevById.get(id);
    if (before === undefined || !deepEqual(before, item)) {
      ymap.set(id, item);
    }
  }
}

/**
 * Read a Y.Map<unknown> back into an ordered array.
 *
 * Y.Map has no insertion order guarantee, so we sort by id to keep
 * the array stable across peers. xyflow accepts any order for its
 * nodes/edges arrays; the sort is purely for determinism in tests.
 */
export function readMap<T extends WithId>(ymap: Y.Map<unknown>): T[] {
  const items: T[] = [];
  ymap.forEach((v) => items.push(v as T));
  return items.sort((a, b) => a.id.localeCompare(b.id));
}

/** Structural (deep) equality by value. */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (
      !deepEqual(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k],
      )
    ) {
      return false;
    }
  }
  return true;
}
